import React from 'react';
import { Cpu, ArrowRight, Cog, Layers } from 'lucide-react';
import { useDbNavigate } from '../../context/DatabaseContext';

const toolGroups = [
  {
    id: 'plc-tools',
    label: 'PLC Tools',
    icon: Cpu,
    tools: [
      {
        slug: 'io-buffer-generation',
        label: 'IO Buffer Generation',
        description: 'Generates PLC import CSVs and structured text for input/output buffer programs from tag and platform data. Outputs PLC Import CSV, Input Buffer ST, and Output Buffer ST files.'
      },
      {
        slug: 'udt-comment-generator',
        label: 'UDT Comment Generation',
        description: 'Parses RSLogix 5000 tag CSV exports and generates comment rows for each UDT tag instance based on Tag XRef member definitions. Supports mode selection, range prompts, and array expansion.'
      },
      {
        slug: 'scada-tag-generation',
        label: 'SCADA Tag Generation',
        description: 'Generates Cimplicity and Ignition tag import files from Tool 2 CSV output. Produces CIM Import (140 cols), IGN First/Second Pass, and DataLog exports per SCADA system.'
      },
      {
        slug: 'simulation-tag-generation',
        label: 'Simulation Tag Generation',
        description: 'Generates SIM_ prefix tag sets for OTS (Over-the-Shoulder) testing from database or Tool 1 CSV input. Outputs PLC Import CSV, Input Buffer ST, and SCADA import (IGN or CIM). SIM tags are deleted post-testing by filtering on "SIM".'
      }
    ]
  },
  {
    id: 'configuration',
    label: 'Configuration',
    icon: Cog,
    tools: [
      {
        slug: 'plc-platforms',
        label: 'PLC Platforms',
        description: 'Manages PLC platform definitions used by the IO Buffer Generator. Defines raw AI/AO min/max values and platform metadata per project.'
      },
      {
        slug: 'plc-tag-xref',
        label: 'Tag XRef',
        description: 'Manages UDT datamember definitions and Tool2 custom flags. Drives the UDT Comment Generator — defines which members get included, their descriptions, and how they are handled.'
      }
    ]
  }
];

const IntegrationDashboardPage = () => {
  const navigate = useDbNavigate();

  const totalTools = toolGroups.reduce((sum, g) => sum + g.tools.filter(t => !t.future).length, 0);

  return (
    <div className="page-container" style={{ padding: '20px' }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        backgroundColor: 'var(--color-bg-white)',
        borderBottom: '1px solid var(--color-gray-200)',
        marginBottom: 'var(--spacing-lg)'
      }}>
        <Layers size={20} style={{ marginRight: '8px', color: 'var(--color-primary)' }} />
        <h1 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--color-primary)' }}>
          Integration
        </h1>
        <span style={{ marginLeft: '12px', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
          {totalTools} tools available
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
        {toolGroups.map(group => {
          const GroupIcon = group.icon;
          return (
            <div key={group.id}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: 'var(--spacing-md)',
                paddingBottom: 'var(--spacing-sm)',
                borderBottom: '2px solid var(--color-primary)'
              }}>
                <GroupIcon size={16} style={{ color: 'var(--color-primary)' }} />
                <span style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                  fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {group.label}
                </span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)' }}>
                  {group.tools.filter(t => !t.future).length} {group.tools.filter(t => !t.future).length === 1 ? 'tool' : 'tools'} available
                </span>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: 'var(--spacing-md)'
              }}>
                {group.tools.map(tool => (
                  <div
                    key={tool.slug}
                    onClick={() => !tool.future && navigate(`/${tool.slug}`)}
                    style={{
                      background: 'var(--color-bg-white)',
                      border: '1px solid var(--color-gray-200)',
                      borderRadius: '6px',
                      padding: 'var(--spacing-md) var(--spacing-lg)',
                      cursor: tool.future ? 'default' : 'pointer',
                      opacity: tool.future ? 0.5 : 1,
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}
                    onMouseEnter={e => {
                      if (!tool.future) {
                        e.currentTarget.style.borderColor = 'var(--color-primary)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'var(--color-gray-200)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontSize: 'var(--text-base)',
                        fontWeight: 600,
                        color: 'var(--color-primary)',
                        fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {tool.label}
                      </span>
                      {!tool.future && <ArrowRight size={16} style={{ color: 'var(--color-gray-400)', flexShrink: 0 }} />}
                      {tool.future && (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', fontStyle: 'italic' }}>
                          Coming soon
                        </span>
                      )}
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: 'var(--text-sm)',
                      color: 'var(--color-gray-600)',
                      lineHeight: 1.5
                    }}>
                      {tool.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IntegrationDashboardPage;
